import React, { useState, useEffect, useCallback } from 'react';
import { auth, db, storage } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import './SpringHockeyForm.css';
import practiceJersey from './assets/images/practice jersey.png';
import practiceJersey2 from './assets/images/practice jersey2.png';
import practiceJerseySizeChart from './assets/images/practice jersey size chart.png';
import apparelTshirt from './assets/images/tshirt.png';
import apparelSweatshirt from './assets/images/sweatshirt.png';
import warmupFront from './assets/images/warmup front.jpeg';
import warmupSide from './assets/images/warmup side.jpeg';

const GLOVE_PRODUCT_IMG = '/images/spring-hockey/bauer-vapor-flylite-gloves.png';
const LIFESTYLE_CHART_IMG = '/images/spring-hockey/bauer-lifestyle-sizing-chart.png';
const FLYLITE_GLOVE_CHART_IMG = '/images/spring-hockey/bauer-flylite-glove-sizing-chart.png';

const POSITIONS = ['Forward', 'Center', 'Left Wing', 'Right Wing', 'Defense', 'Goalie'];

/** Jersey, t-shirt, and sweatshirt — same scale */
const APPAREL_SIZES_YOUTH_THROUGH_ADULT = [
  'Youth Small',
  'Youth Medium',
  'Youth Large',
  'Youth XL',
  'Adult Small',
  'Adult Medium',
  'Adult Large',
  'Adult XL',
  'Adult XXL',
  'Youth Goal Cut',
  'Intermediate Goal Cut',
  'Senior Goal Cut',
];

/** Warmup jacket: one choice from chart (Men / Youth + letter) */
const WARMUP_JACKET_SIZE_OPTIONS = [
  { value: 'youth|XS', label: 'Youth XS' },
  { value: 'youth|S', label: 'Youth S' },
  { value: 'youth|M', label: 'Youth M' },
  { value: 'youth|L', label: 'Youth L' },
  { value: 'men|S', label: 'Men S' },
  { value: 'men|M', label: 'Men M' },
  { value: 'men|L', label: 'Men L' },
  { value: 'men|XL', label: 'Men XL' },
];

/** Bauer lifestyle pants — Men: S–XL */
const MEN_LETTER_SIZES = ['S', 'M', 'L', 'XL'];
/** Bauer lifestyle pants — Youth: XS–XL */
const YOUTH_LETTER_SIZES = ['XS', 'S', 'M', 'L','XL'];

const GLOVE_CATEGORIES = [
  { value: 'junior', label: 'Junior' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'senior', label: 'Senior' },
];

const GLOVE_SIZES_INCHES = ['10', '11', '12', '12.5', '13', '14', '15'];

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

const letterSizesForFit = (fit) => {
  if (fit === 'men') return MEN_LETTER_SIZES;
  if (fit === 'youth') return YOUTH_LETTER_SIZES;
  return [];
};

const emptyForm = () => ({
  playerName: '',
  position: '',
  desiredNumber: '',
  backupDesiredNumber: '',
  jerseySize: '',
  tShirtSize: '',
  sweatshirtSize: '',
  warmupJacketPick: '',
  warmupJacketType: '',
  pantsFit: '',
  pantsSize: '',
  gloveCategory: '',
  gloveSizeInches: '',
  bio: '',
  funFact: '',
  rosterPhotoPermission: false,
});

const SpringHockeyForm = ({ displayName, userEmail }) => {
  const [form, setForm] = useState(emptyForm);
  const [rosterPhoto, setRosterPhoto] = useState(null);
  const [rosterPhotoPreview, setRosterPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [jerseyChartLightboxOpen, setJerseyChartLightboxOpen] = useState(false);

  const loadMine = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoadingMine(true);
    try {
      const q = query(
        collection(db, 'spring_hockey_registrations'),
        where('parentUid', '==', user.uid)
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.submittedAt?.toDate?.() || new Date(a.submittedAt || 0);
        const tb = b.submittedAt?.toDate?.() || new Date(b.submittedAt || 0);
        return tb - ta;
      });
      setMySubmissions(rows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMine(false);
    }
  }, []);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  useEffect(() => {
    if (!jerseyChartLightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setJerseyChartLightboxOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [jerseyChartLightboxOpen]);

  useEffect(() => {
    if (!rosterPhoto) {
      setRosterPhotoPreview(null);
      return;
    }
    const url = URL.createObjectURL(rosterPhoto);
    setRosterPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [rosterPhoto]);

  const update = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'pantsFit') {
        const allowed = letterSizesForFit(value);
        if (next.pantsSize && !allowed.includes(next.pantsSize)) {
          next.pantsSize = '';
        }
      }
      return next;
    });
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user) return;

    const required = [
      ['playerName', 'Player name'],
      ['position', 'Position'],
      ['desiredNumber', 'Desired jersey number'],
      ['jerseySize', 'Jersey size'],
      ['tShirtSize', 'T-shirt size'],
      ['sweatshirtSize', 'Sweatshirt size'],
    ];
    for (const [key, label] of required) {
      if (!String(form[key] || '').trim()) {
        setMessage({ type: 'error', text: `Please fill in: ${label}.` });
        return;
      }
    }
    if (!form.rosterPhotoPermission) {
      setMessage({
        type: 'error',
        text: 'Please confirm permission to use the roster photo on the website.',
      });
      return;
    }
    if (!rosterPhoto) {
      setMessage({ type: 'error', text: 'Please upload a roster photo.' });
      return;
    }
    if (!String(form.bio || '').trim() || !String(form.funFact || '').trim()) {
      setMessage({ type: 'error', text: 'Please add a short bio and a fun fact.' });
      return;
    }
    if (!rosterPhoto.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Roster photo must be an image.' });
      return;
    }
    if (rosterPhoto.size > MAX_PHOTO_BYTES) {
      setMessage({ type: 'error', text: 'Photo must be 10 MB or smaller.' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const rawExt = (rosterPhoto.name.split('.').pop() || 'jpg').toLowerCase();
      const ext = /^[a-z0-9]+$/.test(rawExt) ? rawExt.slice(0, 8) : 'jpg';
      const objectPath = `parents/${user.uid}/springHockey/${Date.now()}.${ext}`;
      const sref = ref(storage, objectPath);
      await uploadBytes(sref, rosterPhoto, {
        contentType: rosterPhoto.type || 'image/jpeg',
      });
      const rosterPhotoUrl = await getDownloadURL(sref);

      const [wjFit, wjLetter] = form.warmupJacketPick
        ? String(form.warmupJacketPick).split('|')
        : ['', ''];

      await addDoc(collection(db, 'spring_hockey_registrations'), {
        parentUid: user.uid,
        parentEmail: user.email || userEmail || '',
        parentDisplayName: displayName || '',
        submittedAt: Timestamp.now(),
        archived: false,
        playerName: form.playerName.trim(),
        position: form.position,
        desiredNumber: String(form.desiredNumber).trim(),
        backupDesiredNumber: String(form.backupDesiredNumber || '').trim(),
        jerseySize: form.jerseySize,
        tShirtSize: form.tShirtSize,
        sweatshirtSize: form.sweatshirtSize,
        warmupJacketPick: form.warmupJacketPick,
        warmupJacketFit: wjFit || '',
        warmupJacketSize: wjLetter || '',
        warmupJacketType: form.warmupJacketType,
        pantsFit: form.pantsFit,
        pantsSize: form.pantsSize,
        gloveCategory: form.gloveCategory,
        gloveSizeInches: form.gloveSizeInches,
        rosterPhotoUrl,
        rosterPhotoPermissionAcknowledged: true,
        rosterPhotoPermissionAt: Timestamp.now(),
        bio: form.bio.trim(),
        funFact: form.funFact.trim(),
      });

      setForm(emptyForm());
      setRosterPhoto(null);
      setMessage({
        type: 'success',
        text: 'Thanks! Your Spring Hockey information was submitted.',
      });
      await loadMine();
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text:
          'Could not submit. Check your connection and Firebase rules (Firestore + Storage). If this continues, contact the team.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const pantsSizeOptions = letterSizesForFit(form.pantsFit);

  return (
    <div className="spring-hockey-form">
      <h2>Spring Hockey — player &amp; gear info</h2>
      <p className="spring-hockey-lead">
        One submission per player. Spring apparel sizes are required below. Warmup jacket, pants, and
        gloves are optional and used for fall season ordering — see the note before those sections. Your
        bio and fun fact may be used on the team website with your permission.
      </p>

      {message.text && (
        <p className={`spring-hockey-message spring-hockey-message-${message.type}`}>{message.text}</p>
      )}

      <form onSubmit={handleSubmit} className="spring-hockey-fields">
        <fieldset className="spring-hockey-fieldset">
          <legend>Player</legend>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Player name <span className="req">*</span>
            </span>
            <input
              type="text"
              value={form.playerName}
              onChange={(e) => update('playerName', e.target.value)}
              autoComplete="name"
              required
            />
          </label>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Position <span className="req">*</span>
            </span>
            <select
              value={form.position}
              onChange={(e) => update('position', e.target.value)}
              required
            >
              <option value="">Select…</option>
              {POSITIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <div className="spring-hockey-row">
            <label className="spring-hockey-label">
              <span className="spring-hockey-label-line">
                Desired jersey # <span className="req">*</span>
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={form.desiredNumber}
                onChange={(e) => update('desiredNumber', e.target.value)}
              />
            </label>
            <label className="spring-hockey-label">
              Backup desired #
              <input
                type="text"
                inputMode="numeric"
                value={form.backupDesiredNumber}
                onChange={(e) => update('backupDesiredNumber', e.target.value)}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="spring-hockey-fieldset">
          <legend>Apparel sizes</legend>
          <p className="spring-hockey-hint">
            Reference photos below. Jersey, t-shirt, and sweatshirt use the same scale: Youth Small
            through Adult XXL. Use <strong>Full-size chart</strong> or open <strong>Jersey size chart</strong>{' '}
            and tap the image for a large, readable view.
          </p>
          <div className="spring-hockey-apparel-gallery" aria-label="Team apparel reference photos">
            <figure className="spring-hockey-apparel-figure">
              <img src={practiceJersey} alt="Practice jersey" />
              <figcaption>Practice jersey</figcaption>
            </figure>
            <figure className="spring-hockey-apparel-figure">
              <img src={practiceJersey2} alt="Practice jersey alternate view" />
              <figcaption>Practice jersey (alternate)</figcaption>
            </figure>
            <figure className="spring-hockey-apparel-figure">
              <img src={apparelTshirt} alt="Team t-shirt" />
              <figcaption>T-shirt</figcaption>
            </figure>
            <figure className="spring-hockey-apparel-figure">
              <img src={apparelSweatshirt} alt="Team sweatshirt" />
              <figcaption>Sweatshirt</figcaption>
            </figure>
          </div>
          <div className="spring-hockey-label">
            <div className="spring-hockey-jersey-row">
              <span className="spring-hockey-label-line" id="spring-hockey-jersey-label">
                Jersey size <span className="req">*</span>
              </span>
              <details className="spring-hockey-jersey-chart-details">
                <summary className="spring-hockey-jersey-chart-summary">
                  Jersey size chart
                </summary>
                <figure className="spring-hockey-jersey-chart-figure">
                  <button
                    type="button"
                    className="spring-hockey-jersey-chart-thumb"
                    onClick={() => setJerseyChartLightboxOpen(true)}
                    aria-label="Open practice jersey size chart full screen for easier reading"
                  >
                    <img src={practiceJerseySizeChart} alt="" />
                  </button>
                  <figcaption className="spring-hockey-jersey-chart-hint">
                    Tap or click the image to view full size
                  </figcaption>
                </figure>
              </details>
              <button
                type="button"
                className="spring-hockey-jersey-fullsize-btn"
                onClick={() => setJerseyChartLightboxOpen(true)}
              >
                Full-size chart
              </button>
            </div>
            <select
              value={form.jerseySize}
              onChange={(e) => update('jerseySize', e.target.value)}
              required
              aria-labelledby="spring-hockey-jersey-label"
            >
              <option value="">Select…</option>
              {APPAREL_SIZES_YOUTH_THROUGH_ADULT.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              T-shirt size <span className="req">*</span>
            </span>
            <select
              value={form.tShirtSize}
              onChange={(e) => update('tShirtSize', e.target.value)}
              required
            >
              <option value="">Select…</option>
              {APPAREL_SIZES_YOUTH_THROUGH_ADULT.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Sweatshirt size <span className="req">*</span>
            </span>
            <select
              value={form.sweatshirtSize}
              onChange={(e) => update('sweatshirtSize', e.target.value)}
              required
            >
              <option value="">Select…</option>
              {APPAREL_SIZES_YOUTH_THROUGH_ADULT.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <div className="spring-hockey-fall-season-note" role="note">
          <h3 className="spring-hockey-fall-season-title">Fall season sizing</h3>
          <p>
            <strong>Warmup jacket</strong>, <strong>pants</strong>, and <strong>hockey gloves</strong>{' '}
            below are for <strong>fall season</strong> gear. These fields are optional for this form;
            fill them in when you are ready to order fall equipment.
          </p>
        </div>

        <fieldset className="spring-hockey-fieldset">
          <legend>Warmup jacket</legend>
          <p className="spring-hockey-hint">
            Optional — fall season. Use the Bauer chart below to pick your warmup jacket size, then
            choose middleweight or lightweight — both use the same sizing.
          </p>
          <figure className="spring-hockey-chart-figure">
            <img
              src={LIFESTYLE_CHART_IMG}
              alt="Bauer lifestyle apparel sizing chart for Men and Youth"
            />
            <figcaption>Bauer lifestyle apparel — Men &amp; Youth</figcaption>
          </figure>

          <div className="spring-hockey-sublegend spring-hockey-label-line" style={{ marginTop: '0.25rem' }}>
            Jacket style <span className="spring-hockey-optional-tag">(optional)</span>
          </div>
          <div className="spring-hockey-jacket-grid" role="radiogroup" aria-label="Warmup jacket style">
            <label
              className={`spring-hockey-jacket-card${form.warmupJacketType === 'middleweight' ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="warmupJacketType"
                value="middleweight"
                checked={form.warmupJacketType === 'middleweight'}
                onChange={() => update('warmupJacketType', 'middleweight')}
              />
              <div className="spring-hockey-jacket-photo">
                <img src={warmupFront} alt="Middleweight warmup jacket, front view" />
              </div>
              <span className="spring-hockey-jacket-card-title">Middleweight</span>
            </label>
            <label
              className={`spring-hockey-jacket-card${form.warmupJacketType === 'lightweight' ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="warmupJacketType"
                value="lightweight"
                checked={form.warmupJacketType === 'lightweight'}
                onChange={() => update('warmupJacketType', 'lightweight')}
              />
              <div className="spring-hockey-jacket-photo">
                <img src={warmupSide} alt="Lightweight warmup jacket, side view" />
              </div>
              <span className="spring-hockey-jacket-card-title">Lightweight</span>
            </label>
          </div>

          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Warmup jacket size <span className="spring-hockey-optional-tag">(optional)</span>
            </span>
            <select
              value={form.warmupJacketPick}
              onChange={(e) => update('warmupJacketPick', e.target.value)}
            >
              <option value="">Select…</option>
              {WARMUP_JACKET_SIZE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <fieldset className="spring-hockey-fieldset">
          <legend>Pants (warmup / team)</legend>
          <p className="spring-hockey-hint">
            Optional — fall season. Use the same Bauer chart as the jacket. Pant size usually matches
            your jacket letter size.
          </p>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Pant — Men or Youth <span className="spring-hockey-optional-tag">(optional)</span>
            </span>
            <select
              value={form.pantsFit}
              onChange={(e) => update('pantsFit', e.target.value)}
            >
              <option value="">Select…</option>
              <option value="men">Men</option>
              <option value="youth">Youth</option>
            </select>
          </label>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Pant size <span className="spring-hockey-optional-tag">(optional)</span>
            </span>
            <select
              value={form.pantsSize}
              onChange={(e) => update('pantsSize', e.target.value)}
              disabled={!form.pantsFit}
            >
              <option value="">{form.pantsFit ? 'Select…' : 'Choose Men or Youth first'}</option>
              {pantsSizeOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <fieldset className="spring-hockey-fieldset spring-hockey-gloves">
          <legend>Hockey gloves</legend>
          <p className="spring-hockey-hint">
            Optional — fall season. Reference model: <strong>Bauer Vapor Flylite</strong>. Choose glove
            line (Junior / Intermediate / Senior), then the size in inches. Use the chart to
            cross-check age, height, and hand length.
          </p>
          <figure className="spring-hockey-glove-figure">
            <img src={GLOVE_PRODUCT_IMG} alt="Bauer Vapor Flylite hockey gloves (reference)" />
            <figcaption>Product reference — Bauer Vapor Flylite</figcaption>
          </figure>
          <figure className="spring-hockey-chart-figure spring-hockey-chart-figure--glove">
            <img
              src={FLYLITE_GLOVE_CHART_IMG}
              alt="Bauer Flylite glove sizing: Senior, Intermediate, and Junior size charts"
            />
            <figcaption>Bauer Flylite — glove sizing by category</figcaption>
          </figure>
          <div className="spring-hockey-row spring-hockey-glove-row">
            <label className="spring-hockey-label">
              <span className="spring-hockey-label-line">
                Glove category <span className="spring-hockey-optional-tag">(optional)</span>
              </span>
              <select
                value={form.gloveCategory}
                onChange={(e) => update('gloveCategory', e.target.value)}
              >
                <option value="">Select…</option>
                {GLOVE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="spring-hockey-label">
              <span className="spring-hockey-label-line">
                Glove size <span className="spring-hockey-optional-tag">(optional)</span>
              </span>
              <select
                value={form.gloveSizeInches}
                onChange={(e) => update('gloveSizeInches', e.target.value)}
              >
                <option value="">Select inches…</option>
                {GLOVE_SIZES_INCHES.map((n) => (
                  <option key={n} value={n}>
                    {n}&quot;
                  </option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset className="spring-hockey-fieldset">
          <legend>Roster &amp; website</legend>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Roster photo <span className="req">*</span>
            </span>
            <span className="spring-hockey-sublabel">
              Clear, recent photo of the player (head and shoulders is fine). May be used temporarily
              until official roster photos.
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                setRosterPhoto(f || null);
              }}
            />
            {rosterPhotoPreview && (
              <img
                src={rosterPhotoPreview}
                alt="Selected roster preview"
                className="spring-hockey-preview"
              />
            )}
          </label>
          <label className="spring-hockey-checkbox">
            <input
              type="checkbox"
              checked={form.rosterPhotoPermission}
              onChange={(e) => update('rosterPhotoPermission', e.target.checked)}
            />
            <span>
              We give the Glass City Gladiators permission to use the photo uploaded above on the team
              website (including roster and promotional pages). <span className="req">*</span>
            </span>
          </label>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Short bio <span className="req">*</span>
            </span>
            <textarea
              rows={4}
              value={form.bio}
              onChange={(e) => update('bio', e.target.value)}
              placeholder="A few sentences for the website roster..."
            />
          </label>
          <label className="spring-hockey-label">
            <span className="spring-hockey-label-line">
              Fun fact <span className="req">*</span>
            </span>
            <textarea
              rows={2}
              value={form.funFact}
              onChange={(e) => update('funFact', e.target.value)}
              placeholder="Something fun to share on the site..."
            />
          </label>
        </fieldset>

        <button type="submit" className="parents-button spring-hockey-submit" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Spring Hockey info'}
        </button>
      </form>

      <section className="spring-hockey-prior" aria-label="Your previous submissions">
        <h3>Your submissions</h3>
        {loadingMine ? (
          <p className="spring-hockey-muted">Loading…</p>
        ) : mySubmissions.length === 0 ? (
          <p className="spring-hockey-muted">No submissions yet.</p>
        ) : (
          <ul className="spring-hockey-prior-list">
            {mySubmissions.map((row) => (
              <li key={row.id}>
                <strong>{row.playerName || 'Player'}</strong>
                {' · '}
                {row.submittedAt?.toDate
                  ? row.submittedAt.toDate().toLocaleString()
                  : 'Submitted'}
                {row.desiredNumber != null && row.desiredNumber !== '' && (
                  <>
                    {' · '}
                    #{row.desiredNumber}
                  </>
                )}
                {row.rosterPhotoUrl && (
                  <>
                    {' '}
                    <a href={row.rosterPhotoUrl} target="_blank" rel="noopener noreferrer">
                      Photo
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {jerseyChartLightboxOpen ? (
        <div
          className="spring-hockey-chart-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Practice jersey size chart"
          onClick={() => setJerseyChartLightboxOpen(false)}
        >
          <button
            type="button"
            className="spring-hockey-chart-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setJerseyChartLightboxOpen(false);
            }}
            aria-label="Close chart"
          >
            ×
          </button>
          <div
            className="spring-hockey-chart-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={practiceJerseySizeChart}
              alt="Practice jersey size chart with measurements by size"
            />
          </div>
          <p className="spring-hockey-chart-lightbox-hint">Click outside the image or press Esc to close</p>
        </div>
      ) : null}
    </div>
  );
};

export default SpringHockeyForm;
